import { Action, Constructor, Dictionary, IDisposable, RecursiveDiff, Tuple } from '@skylab-shared/types';
import { BehaviorSubject, Observable } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { ConsoleLogger } from '../../logger';
import {
    CollectionChangedEventArgs,
    CollectionChangingAction,
    ObservableCollection,
    PropertyChangedEventArgs,
    PropertyChangingEventArgs
} from '../../observable';
import { ReadonlyChecker } from '../../rules/src/readonly-checker';
import { ValidationResult, ValidationSource, Validations, Validator, Violation, ViolationSeverity } from '../../validation';
import { Weaver } from './../../aop';
import { LocalContext } from './../../context';
import { Domain, ModelBase, ModelSerializer } from './../../model';
import { ExternalRuleViolation, ResponseConflictObject, SaveConflictResponse } from './../../repo';
import {
    CanExecuteEngine, CanExecuteRuleResult,
    CollectionChangedJoinPoint, CollectionChangedJoinPointContext,
    PropertyChangedJoinPoint, PropertyChangedJoinPointContext,
    PropertyChangingJoinPoint, PropertyChangingJoinPointContext, ReadonlyEngine, ReadonlyRuleResult,
    RequiredEngine, RuleDefinition, RuleResult, ValidationEngine
} from './../../rules';
import { BusinessObjectPart, BusinessObjectPartContext } from './business-object-part';
import { CoreFeatureManager } from './core-feature-manager';

export interface BusinessObjectConfig<TModel extends ModelBase> {
    readonly modelType: Constructor<TModel>;
    readonly mixins?: typeof BusinessObjectBase<ModelBase>[];
}

export interface InternalBusinessObjectConfig<TModel extends ModelBase> extends BusinessObjectConfig<TModel> {
    readonly domain: Domain;
}

export enum BusinessObjectState {
    Uninitialized = 1, // model not created
    New, // new model
    Loaded, // loaded model
}

export abstract class BusinessObjectBase<TModel extends ModelBase> implements ValidationSource<ModelBase>, IDisposable {
    private internalModel: TModel | null = null;
    private internalState = new BehaviorSubject(BusinessObjectState.Uninitialized);
    private internalIsDirty: boolean;

    private readonly unsubscribeFunctions: Tuple<any, Function>[];

    protected weaver: Weaver;

    protected readonly logger: ConsoleLogger;
    protected readonly validator: Validator = new Validator();
    protected readonly validation: ValidationEngine<this, TModel>;
    protected readonly required: RequiredEngine<this, TModel>;
    protected readonly readonly: ReadonlyEngine<this, TModel>;
    protected readonly canExecute: CanExecuteEngine<this, TModel>;
    protected readonly propertyChanged: CoreFeatureManager;
    protected readonly collectionChanged: CoreFeatureManager;
    protected readonly readonlyChecker: ReadonlyChecker<this, TModel>;

    protected readonly config: InternalBusinessObjectConfig<TModel>;
    protected readonly parts: BusinessObjectPart<ModelBase>[] = [];

    private _localContext!: LocalContext;

    public constructor(
        weaver: Weaver
    ) {
        this.weaver = weaver;
        this.required = new RequiredEngine(weaver);
        this.validation = new ValidationEngine(weaver);
        this.readonly = new ReadonlyEngine(weaver);
        this.readonlyChecker = new ReadonlyChecker(this.readonly, weaver);
        this.canExecute = new CanExecuteEngine(weaver);
        this.logger = new ConsoleLogger(this.constructor.name);

        this.config = Reflect.getMetadata('skl:bo', this.constructor);
        this.propertyChanged = new CoreFeatureManager();
        this.collectionChanged = new CoreFeatureManager();

        this.unsubscribeFunctions = [];

        this.internalModel = null;
        this.internalIsDirty = false;
        this.logger.info(`BO created.`);
    }

    //#region Properties

    public get model(): TModel {
        return this.internalModel!;
    }

    public get state$(): Observable<BusinessObjectState> {
        return this.internalState.asObservable();
    }

    public get state(): BusinessObjectState {
        return this.internalState.value;
    }

    public get isDirty(): boolean {
        return this.internalIsDirty;
    }

    public get isValid(): boolean {
        return !this.validationResult
            .some(r => r.ruleViolation.violation.severity === ViolationSeverity.Error);
    }

    public get validationResult(): ObservableCollection<ValidationResult<ModelBase>> {
        return this.validation.validationResult;
    }

    public get requiredProperties(): ObservableCollection<RuleResult> {
        return this.required.requiredProperties;
    }

    public get readonlyProperties(): ObservableCollection<ReadonlyRuleResult & RuleResult> {
        return this.readonly.readonlyProperties;
    }

    public isPropertyReadonly<T extends ModelBase>(source: T, propertyName: keyof T): boolean {
        return this.readonlyProperties.some(elem => (elem.propertyName === propertyName) && (elem.source === source) && elem.readonly);
    }

    public get canExecuteMethods(): ObservableCollection<CanExecuteRuleResult> {
        return this.canExecute.result;
    }

    //#endregion

    public get localContext(): LocalContext {
        if (!this._localContext)
            throw new Error('Local context is not set.');
        return this._localContext;
    }

    public setContext(localContext: LocalContext): void {
        this._localContext = localContext;
    }

    public async waitForProcessingCompletionAsync(): Promise<void> {
        await Promise.all([
            this.validation.waitAsync(),
            this.required.waitAsync(),
            this.readonly.waitAsync(),
            this.canExecute.waitAsync()
        ]);
    }

    public asPojso(): RecursiveDiff<TModel, ModelBase> {
        return ModelSerializer.toPlainObject(this.model);
    }

    //#region Dealing with model

    public async createModelAsync(): Promise<ModelBase> {
        if (this.model !== null)
            throw new Error('Model has already been created.');

        this.internalModel = <TModel>new this.config.modelType();

        try {
            this.observeDomain(this.model);
            this.attachToRulesEngines();
            await this.initializeAsync();
            this.startRulesProcessing();
            this.queueRulesValidation();

            this.internalState.next(BusinessObjectState.New);

            return this.model;
        } catch (err) {
            this.logger.error(err);
            throw err;
        }
    }

    public async attachModelAsync(model: TModel): Promise<void> {
        if (this.model !== null)
            throw new Error('Model has already been created.');

        this.internalModel = model;

        try {
            this.observeDomain(this.model);
            this.attachToRulesEngines();
            await this.initializeAsync();
            this.startRulesProcessing();
            this.queueRulesValidation();

            this.internalState.next(BusinessObjectState.Loaded);
        } catch (err) {
            this.logger.error(err);
            throw err;
        }
    }

    //#endregion

    //#region Rules

    public attachReadonlyChecker(): void {
        this.readonlyChecker.attach();
    }

    public removeServerRuleViolations(): void {
        this.validation.removeExternalValidationResults('external-error');
    }

    public onSaveConflictResponse(response: SaveConflictResponse<unknown>): void {
        response.conflicts?.forEach(this.handleConflictSaveResponse.bind(this));
    }

    protected handleConflictSaveResponse(conflict: ResponseConflictObject<unknown>) { }

    public addExternalRuleViolations<TRuleModel = TModel>(violations: Dictionary<ExternalRuleViolation<TRuleModel>[]>): void {
        this.config.domain.getModels(this.model).filter(([model]) => violations[model.sun]).forEach(([model]) => {
            violations[model.sun].filter(Boolean).forEach(elem => {
                this.validation.addExternalValidationResult({
                    source: model,
                    propertyName: <keyof ModelBase>elem.property,
                    ruleViolation: {
                        rule: null,
                        context: null,
                        violation: elem.violation
                    }
                }, 'external-error');
            });
        });
    }

    private attachToRulesEngines(): void {
        this.validation.attachBusinessObject(this);
        this.required.attachBusinessObject(this);
        this.readonly.attachBusinessObject(this);
        this.canExecute.attachBusinessObject(this);
    }

    private startRulesProcessing(): void {
        this.validation.startProcessing();
        this.required.startProcessing();
        this.readonly.startProcessing();
        this.canExecute.startProcessing();
    }

    public queueRulesValidation(): void {
        this.validation.queueValidation();
        this.readonly.queueValidation();
        this.required.queueValidation();
        this.canExecute.queueValidation();
    }

    //#endregion

    //#region IDisposable

    public dispose(): void {
        this.unsubscribeFunctions.forEach(tuple => tuple[1]());
        this.validation.dispose();
        this.readonly.dispose();
        this.readonlyChecker.dispose();
        this.required.dispose();
        this.canExecute.dispose();
        this.localContext.dispose();
        this.internalState?.unsubscribe();

        this.logger.info(`BO disposed.`);
    }

    //#endregion

    //#region Protected method

    protected async initializeAsync(): Promise<void> {
        const validationRuleDefinitions = findValidationRules(this.config.domain);
        validationRuleDefinitions.forEach(ruleDefinition => this.validation.addRule(
            ruleDefinition.triggeredBy,
            ruleDefinition.effectOn,
            ruleDefinition.rule.funcAsync,
            ruleDefinition.args
        ));

        await this.prepareBoParts();
    }

    protected async prepareBoParts(): Promise<void> {
        for (const boPart of this.parts) {
            await boPart.initializeAsync(this.createBoPartContext());
            this.registerListening(() => boPart.dispose());
        }
    }

    protected registerListening(unsubscribeFunction: Function): void;
    protected registerListening(instance: any, unsubscribeFunction: Function): void;
    protected registerListening(instanceOrUnsubscribeFunction: any, unsubscribeFunction?: any): void {
        this.unsubscribeFunctions.push(
            arguments.length === 2
                ? [instanceOrUnsubscribeFunction, unsubscribeFunction]
                : [void 0, instanceOrUnsubscribeFunction]
        );
    }

    protected unregisterListening(instance: any): void {
        this.unsubscribeFunctions
            .filter(uf => uf[0] === instance)
            .forEach(uf => this.unsubscribeFunctions.splice(this.unsubscribeFunctions.indexOf(uf), 0) && uf[1]());
    }

    //#endregion

    //#region Change detection

    private observeDomain(rootModel: TModel): void {
        this.config.domain.getModels(rootModel)
            .forEach(([model]) => this.observeModel(model));
        this.config.domain.getCollections(rootModel)
            .forEach(([collection, propertyName]) => this.observeCollection(collection, propertyName));
    }

    private observeModel(model: ModelBase): void {
        if (model.isModified && (this.state !== BusinessObjectState.Uninitialized)) {
            this.logger.warn(`You should not add modified model to domain (BO: ${this.constructor.name}, Model: ${model.constructor.name}). This may cause undesirable behavior.`);
        }

        this.registerListening(
            model,
            model.onPropertyChanging(e => this.onPropertyChanging(e))
        );
        this.registerListening(
            model,
            model.onPropertyChanged(e => this.onPropertyChanged(e))
        );
    }

    private unobserveModel(model: ModelBase): void {
        this.unregisterListening(model);
    }

    private observeCollection(collection: ObservableCollection<any>, propertyName: string): void {
        this.registerListening(
            collection,
            collection.onCollectionChanged(e => this.onCollectionChanged(e, propertyName))
        );
    }

    private unobserveCollection(collection: ObservableCollection<ModelBase>): void {
        this.unregisterListening(collection);
    }

    private onPropertyChanging(e: PropertyChangingEventArgs): void {
        const propertyChangingCtx = new PropertyChangingJoinPointContext(this, <ModelBase>e.sender, e.propertyName, e.newValue);
        this.weaver.weave(new PropertyChangingJoinPoint(propertyChangingCtx));

        if (propertyChangingCtx.canceled) {
            e.cancel = propertyChangingCtx.canceled;
            return;
        }

        if (e.newValue instanceof ModelBase) {
            const models = this.config.domain.getModels(e.newValue);
            models.forEach(([model]) => this.observeModel(model));

            const collections = this.config.domain.getCollections(e.newValue);
            collections.forEach(([collection, p]) => this.observeCollection(collection, p));
        }
        if (e.oldValue instanceof ModelBase) {
            const models = this.config.domain.getModels(e.oldValue);
            models.forEach(([model]) => this.unobserveModel(model));

            const collections = this.config.domain.getCollections(e.oldValue);
            collections.forEach(([collection, p]) => this.unobserveCollection(collection));
        }

        if (e.newValue instanceof ObservableCollection) {
            this.observeCollection(e.newValue, e.propertyName);
            e.newValue.forEach(model => this.observeModel(model));
        }
        if (e.oldValue instanceof ObservableCollection) {
            this.unobserveCollection(e.oldValue);
            e.oldValue.forEach(model => this.unobserveModel(model));
        }
    }

    private onPropertyChanged(e: PropertyChangedEventArgs): void {
        this.setAsDirty();

        const ctx = new PropertyChangedContext(<ModelBase>e.sender, <keyof ModelBase>e.propertyName);
        this.propertyChanged.processCoreFeatures(ctx);

        const propertyChangedCtx = new PropertyChangedJoinPointContext(this, <ModelBase>e.sender, e.propertyName, e.oldValue);
        this.weaver.weave(new PropertyChangedJoinPoint(propertyChangedCtx));
    }

    private onCollectionChanged(e: CollectionChangedEventArgs, propertyName: string): void {
        this.setAsDirty();

        e.newItems && e.newItems
            .filter(item => item instanceof ModelBase)
            .forEach(model => this.observeModel(<ModelBase>model));
        e.oldItems && e.oldItems
            .filter(item => item instanceof ModelBase)
            .forEach(model => this.unobserveModel(<ModelBase>model));

        const ctx = new CollectionChangedContext<ModelBase>(
            this.model,
            <keyof ModelBase>propertyName,
            <ObservableCollection<any>>e.sender,
            e.action,
            e.oldItems,
            e.newItems
        );
        this.collectionChanged.processCoreFeatures(ctx);

        const collectionChangedCtx = new CollectionChangedJoinPointContext(
            this,
            <ObservableCollection<any>>e.sender,
            e.action,
            e.newItems,
            e.oldItems
        );
        this.weaver.weave(new CollectionChangedJoinPoint(collectionChangedCtx));
    }

    //#endregion

    //#region Business handlers

    protected handle(type: 'property-changed', handler: Action<PropertyChangedContext>): void;
    protected handle(type: 'collection-changed', handler: Action<CollectionChangedContext>): void;
    protected handle(type: any, handler: any): void {
        if (this.state !== BusinessObjectState.Uninitialized)
            throw new Error('Cannot register handler. BO is already initialized.');
        switch (type) {
            case 'property-changed': this.propertyChanged.listen(handler as Action<PropertyChangedContext>); break;
            case 'collection-changed': this.collectionChanged.listen(handler as Action<CollectionChangedContext>); break;
            default: throw new RangeError();
        }
    }

    //#endregion
    private setAsDirty(): void {
        if (this.state !== BusinessObjectState.Uninitialized) {
            this.internalIsDirty = true;
        }
    }

    protected createBoPartContext(): BusinessObjectPartContext<TModel> {
        return {
            bo: this,
            coreFeature: {
                propertyChanged: this.propertyChanged,
                collectionChanged: this.collectionChanged
            },
            engine: {
                validation: this.validation,
                readonly: this.readonly,
                required: this.required,
                readonlyChecker: this.readonlyChecker
            }
        };
    }
}

//#region Handlers

export abstract class HandlerContext<TModel extends ModelBase = ModelBase> {
    public constructor(
        public readonly model: TModel,
        public readonly propertyName: Extract<keyof TModel, string>
    ) { }
}

export class PropertyChangedContext<TModel extends ModelBase = ModelBase> extends HandlerContext<TModel> { }

export class CollectionChangedContext<TModel extends ModelBase = ModelBase, TCollectionModel = any> {
    public constructor(
        public readonly model: TModel,
        public readonly propertyName: keyof TModel,
        public readonly collection: ObservableCollection<TCollectionModel>,
        public readonly action: CollectionChangingAction,
        public readonly oldItems: TCollectionModel[],
        public readonly newItems: TCollectionModel[]
    ) { }
}

//#endregion

//#region Helpers

export const findValidationRules = (domain: Domain): RuleDefinition<Constructor<Violation>>[] => {
    const visitedModelTypes: Constructor<ModelBase, any[]>[] = [];
    const ruleDefinitions: RuleDefinition<Constructor<Violation>>[] = [];
    domain.traverseDomainNodes(node => {
        if (!Reflect.hasOwnMetadata('skl:validations', node.modelType))
            return;
        if (visitedModelTypes.indexOf(node.modelType) > -1)
            return;
        visitedModelTypes.push(node.modelType);
        const validations = <Validations>Reflect.getMetadata('skl:validations', node.modelType);
        Object.getOwnPropertyNames(validations).forEach(propertyName => {
            const validationRules = validations[propertyName];
            validationRules.forEach(validationRule => {
                const [rule, args] = validationRule;
                ruleDefinitions.push({
                    id: uuid(),
                    triggeredBy: [[node.modelType, <keyof ModelBase>propertyName]],
                    effectOn: [[node.modelType, <keyof ModelBase>propertyName]],
                    rule: {
                        funcAsync: rule.async ? ctx => rule.validateAsync!(ctx, args) : ctx => Promise.resolve(rule.validate!(ctx, args)),
                        func: rule.async ? ctx => rule.validateAsync!(ctx, args) as unknown as boolean : ctx => rule.validate!(ctx, args),
                    },
                    args: rule.violationType
                });
            });
        });
    });
    return ruleDefinitions;
};

//#endregion
